import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import { decodeUsableToken } from '@shared/helpers/token-expiry.helper';
import { CookieConsentService } from '@shared/services/cookie-consent.service';
import { IFrameService } from '@shared/services/iframe.service';
import { CookieService } from 'ngx-cookie-service';
import {
  LocalStorage,
  LocalStorageService,
  SessionStorageService
} from 'ngx-webstorage';

@Injectable({
  providedIn: 'root'
})
export class CommonEnvironmentsService {
  @LocalStorage() public token: string;

  public readonly IS_PRODUCTION: boolean = Boolean(environment.IS_PRODUCTION);
  public readonly VERSION: string = String(environment.VERSION);
  public readonly ENDPOINTS_SOCKET: string = String(
    environment.ENDPOINTS.SOCKET
  );

  public readonly ENDPOINTS_ASSETS: string = String(
    environment.ENDPOINTS.ASSETS
  );

  public readonly ENDPOINTS_APP: string = String(
    environment.ENDPOINTS.FRONT_END.APP
  );

  public readonly ENDPOINTS_REFERENCES: string = String(
    environment.ENDPOINTS.FRONT_END.REFERENCES
  );

  public APPS: any = [
    {
      src: `${this.ENDPOINTS_ASSETS}/images/vitay.io/logo-turquoise.svg`,
      label: 'feedback',
      url: this.getDomainUrl(environment.ENDPOINTS.FRONT_END.FEEDBACK)
    },
    {
      src: `${this.ENDPOINTS_ASSETS}/images/vitay.io/logo-green.svg`,
      label: 'references',
      url: this.getDomainUrl(environment.ENDPOINTS.FRONT_END.REFERENCES)
    },
    // {
    //   src: `${this.ENDPOINTS_ASSETS}/images/vitay.io/logo-purple.svg`,
    //   label: 'jobs',
    //   url: environment.ENDPOINTS.FRONT_END.JOBS
    // },
    {
      src: `${this.ENDPOINTS_ASSETS}/images/vitay.io/logo-blue.svg`,
      label: 'configuration',
      url: this.getDomainUrl(environment.ENDPOINTS.FRONT_END.CONFIGURATION)
    }
  ];

  constructor(
    private readonly _cookies: CookieService,
    private readonly _ls: LocalStorageService,
    private readonly _iframe: IFrameService,
    private readonly _ss: SessionStorageService,
    private readonly _cookieConsent: CookieConsentService
  ) {}

  public getToken(token?: string) {
    if (this._iframe.isIframe) {
      return this.token;
    }

    const name = token || this.tokenLabel();
    const values = this.cookieValues(name);

    if (values.length <= 1) {
      return this._cookies.get(name);
    }

    // Several cookies share the name when a parent-domain cookie (an older
    // build, or production's admin.vitay.io session seen from
    // staging.admin.vitay.io) sits next to this host's own. The browser lists
    // the older one first and a first-match read would hand back a stale
    // token, so prefer whichever is still usable.
    return (
      values.find((value) => decodeUsableToken(value) !== null) ?? values[0]
    );
  }

  // Every value document.cookie carries under `name`, in browser order.
  private cookieValues(name: string): string[] {
    const prefix = `${encodeURIComponent(name)}=`;

    return document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .filter((cookie) => cookie.startsWith(prefix))
      .map((cookie) => {
        const raw = cookie.slice(prefix.length);
        try {
          return decodeURIComponent(raw);
        } catch {
          return raw;
        }
      });
  }

  // The cookie that holds the session for the app served at this hostname.
  public tokenLabel(): string {
    const href = window.location.href;

    if (href.indexOf(':4000') > -1 || href.indexOf('admin.') > -1) {
      return 'admin-token';
    }

    if (
      // Configuration
      href.indexOf(':4300') > -1 ||
      href.indexOf('configuration.') > -1 ||
      // Feedback
      href.indexOf(':4400') > -1 ||
      href.indexOf('feedback.') > -1 ||
      // References
      href.indexOf(':4500') > -1 ||
      href.indexOf('references.') > -1
    ) {
      return 'recruiter-token';
    }

    return 'candidate-token';
  }

  public setToken(label: string, token: string, isLax: boolean = true) {
    if (this._iframe.isIframe) {
      return (this.token = token);
    }

    // Required for iframe
    if (label !== 'mfa') {
      this.token = token;
    }

    // A strict (per-app) cookie must be host-only, i.e. carry no Domain
    // attribute at all: an explicit Domain — even the host's own — makes
    // the browser send it to every subdomain too, so production's
    // admin.vitay.io session reached staging.admin.vitay.io and shadowed
    // its login. Only the lax (cross-app) cookies are shared on the parent.
    const options: Parameters<CookieService['set']>[2] = {
      expires: 15,
      path: '/',
      sameSite: isLax ? 'Lax' : 'Strict'
      // Not working with secure
      // secure: true,
    };

    if (isLax) {
      options.domain = this.cookieDomain(label);
    }

    this._cookies.set(label, token, options);
  }

  protected hostname(): string {
    return window.location.hostname;
  }

  // The Domain attribute setToken writes each lax (cross-app) cookie with.
  // Deleting a cookie requires the very same attribute, so cleanStorage()
  // reuses this — a delete without it only clears host-only cookies and the
  // token survives (the long-standing "can't delete cross apps cookies"
  // problem).
  private cookieDomain(label: string): string {
    const hostname = this.hostname();

    let domain = 'vitay.io';
    if (hostname.indexOf('vitay.co.uk') > -1) {
      domain = 'vitay.co.uk';
    } else if (environment.ENVIRONMENT !== 'development' || label === 'mfa') {
      const splitted = hostname.split('.');

      if (
        hostname.indexOf('probitypeople') > -1 ||
        hostname.indexOf('mie') > -1
      ) {
        domain = `${splitted[splitted.length - 3]}.${
          splitted[splitted.length - 2]
        }.${splitted[splitted.length - 1]}`;
      } else {
        domain = `${splitted[splitted.length - 2]}.${
          splitted[splitted.length - 1]
        }`;
      }
    }

    return domain;
  }

  public getHostname(): string {
    // TODO: consider removing this logic, seems unecessary
    const index = environment.ENVIRONMENT === 'dev' ? 2 : 1;
    return window.location.hostname.split('.')[index] || 'vitay';
  }

  public cleanStorage() {
    // Consent is per-browser, not per-account: the wipe below must not
    // discard it, or the banner re-appears after every logout/token expiry.
    const restoreConsent = this._cookieConsent.preserve();

    this._ls.clear();

    this._ss.clear();
    // this._ss.clear('user');
    // this._ss.clear('hiringfirm');
    // this._ss.clear('referencerelationships');
    // this._ss.clear('settings');

    // Host-only cookies (no Domain attribute).
    this._cookies.deleteAll('/');

    // Cookies written with an explicit Domain: a delete must carry the same
    // attribute or the browser leaves them untouched, and a surviving expired
    // token re-triggers the 401 wipe on every visit — a permanent redirect
    // loop no amount of "logging out" escapes. Cover the lax domains and
    // every parent of this hostname: older builds wrote strict cookies with
    // Domain=<hostname>, and a sibling deployment's cookie on an intermediate
    // parent (production's admin.vitay.io, seen from staging.admin.vitay.io)
    // is sent here as well.
    const domains = new Set([
      this.cookieDomain(''),
      this.cookieDomain('mfa'),
      ...this.hostnameAndParents()
    ]);
    domains.forEach((domain) => this._cookies.deleteAll('/', domain));

    restoreConsent();
  }

  // The hostname and each parent domain a cookie could have been scoped to
  // (`staging.admin.vitay.io` → itself, `admin.vitay.io`, `vitay.io`). A bare
  // TLD can never hold a cookie, so single-label suffixes are left out.
  private hostnameAndParents(): string[] {
    const hostname = this.hostname();
    const labels = hostname.split('.');
    const parents = labels
      .map((_label, index) => labels.slice(index).join('.'))
      .filter((domain) => domain.split('.').length >= 2);

    return Array.from(new Set([hostname, ...parents]));
  }

  private getDomainUrl(url: string) {
    if (environment.ENVIRONMENT === 'production') {
      const hostname = window.location.hostname;
      const splitted = hostname.split('.');
      const domain = `${splitted[splitted.length - 2]}.${
        splitted[splitted.length - 1]
      }`;

      const isWhiteLabeled = ['vitay', 'mintzscreeningservices'].includes(
        splitted[splitted.length - 2]
      );

      if (isWhiteLabeled) {
        url = url.replace(/vitay.(io|ca)/gi, domain);
      }
    }

    return url;
  }
}
